import React, { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "antoCookieConsent";

const defaultConsent = {
  necessary: true,
  performance: false,
  targeting: false,
  functional: false,
};

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(defaultConsent);
  const [hasResponded, setHasResponded] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setConsent({ ...defaultConsent, ...parsed.consent });
        setHasResponded(true);
      }
    } catch (e) {
      console.error("Failed to read cookie consent from storage", e);
    }
  }, []);

  const persist = (newConsent, shouldReload) => {
    const payload = {
      consent: newConsent,
      timestamp: new Date().toISOString(),
    };
    const prevConsent = consent;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setConsent(newConsent);
    setHasResponded(true);
    setIsPreferencesOpen(false);

    // If any category went from true -> false, scripts already loaded
    // can't be cleanly unloaded, so reload the page to start fresh.
    const revoked = Object.keys(newConsent).some(
      (key) => prevConsent[key] && !newConsent[key]
    );
    if (revoked || shouldReload) {
      window.location.reload();
    }
  };

  const acceptAll = () => {
    persist({ necessary: true, performance: true, targeting: true, functional: true });
  };

  const rejectNonEssential = () => {
    persist({ necessary: true, performance: false, targeting: false, functional: false });
  };

  const saveChoices = (choices) => {
    persist({ ...choices, necessary: true });
  };

  const openPreferences = () => setIsPreferencesOpen(true);
  const closePreferences = () => setIsPreferencesOpen(false);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasResponded,
        isPreferencesOpen,
        acceptAll,
        rejectNonEssential,
        saveChoices,
        openPreferences,
        closePreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used inside CookieConsentProvider");
  return ctx;
}